import { useId, useMemo, useState } from 'react'
import { BookOpen, LifeBuoy, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { Ticket as ContractTicket, TicketMessage } from '@cattle/contracts'

import {
  Avatar,
  BarList,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  FilterBar,
  FilterPills,
  KeyValue,
  LoadingSkeleton,
  Modal,
  Money,
  PageHeader,
  QueryBoundary,
  SearchInput,
  Section,
  Select,
  StatusBadge,
  TextInput,
  useSearchTerm,
  useUrlFilter,
  type Column,
} from '../../../components'
import {
  queryKeys,
  useAskedAbout,
  useBusinesses,
  useTickets,
  type Ticket,
  type TicketChannel,
} from '../../../fixtures'
import { get, patch, post, shouldRetry, type ApiRequestError } from '../../../lib/apiClient'
import { t } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'
import { routes } from '../../../lib/routes'

/**
 * P-09 Support.
 *
 * Tickets arrive on WhatsApp, because that is where Pakistani traders actually
 * are — so the channel is a named word on every row, not an icon to decode.
 * Each row opens straight into that business's book: the question and the
 * evidence in one place.
 *
 * ---------------------------------------------------------------------------
 * G22 — A REPLY IS RECORDED, NOT SENT
 * ---------------------------------------------------------------------------
 * `POST /support/tickets/:id/messages` stores a `support_messages` row and
 * moves the ticket's status. Delivering it to the trader's phone is a WhatsApp
 * message, and no provider, template or consent model has been chosen — the
 * server's only `SupportNotifier` answers `{ delivered: false, reason:
 * 'NO_PROVIDER' }` and logs it. That fact never reaches the wire (the 201
 * carries the message, not the delivery result), so this screen states the one
 * thing that is true today: the reply is on the ticket, and nobody's phone has
 * buzzed. Telling support otherwise would end the conversation from our side
 * while the trader is still waiting.
 */

const CONTROL =
  'w-full rounded-[10px] border border-glass-border bg-glass px-3.5 py-2.5 text-[13.5px] font-semibold leading-relaxed text-dark outline-none placeholder:font-medium placeholder:text-light'

/*
  THREE statuses, not two. `TicketStatus` is the contract's own
  ['OPEN','ANSWERED','CLOSED'], and a closed ticket is a third thing: it is not
  waiting on anybody, and it is not sitting answered waiting to be closed. The
  screen has to be able to say so, or CLOSED silently borrows ANSWERED's badge,
  ANSWERED's count and ANSWERED's row action.
*/
type TicketFilter = 'OPEN' | 'ANSWERED' | 'CLOSED' | 'ALL'
const TICKET_FILTERS: readonly TicketFilter[] = ['OPEN', 'ANSWERED', 'CLOSED', 'ALL']

/** Codes are stored; the label is looked up. All three statuses of them. */
function statusLabel(status: Ticket['status']): string {
  switch (status) {
    case 'OPEN':
      return t('p.support.open')
    case 'ANSWERED':
      return t('p.support.answered')
    case 'CLOSED':
      return t('p.support.closed')
  }
}

const STATUS_TONE: Record<Ticket['status'], 'warn' | 'ok' | 'neutral'> = {
  OPEN: 'warn',
  ANSWERED: 'ok',
  CLOSED: 'neutral',
}

/** Codes are stored; the channel's label is looked up. All four of them. */
function channelLabel(channel: TicketChannel): string {
  switch (channel) {
    case 'WHATSAPP':
      return t('p.support.channelWHATSAPP')
    case 'PHONE':
      return t('p.support.channelPHONE')
    case 'EMAIL':
      return t('p.support.channelEMAIL')
    default:
      return t('p.support.channelIN_APP')
  }
}

export function SupportScreen() {
  const tickets = useTickets()
  const askedAbout = useAskedAbout()
  const businesses = useBusinesses()
  const navigate = useNavigate()

  const term = useSearchTerm()
  const [filter, setFilter] = useUrlFilter<TicketFilter>('show', 'OPEN', TICKET_FILTERS)

  const [logOpen, setLogOpen] = useState(false)
  const [logBusiness, setLogBusiness] = useState('')
  const [logPerson, setLogPerson] = useState('')
  const [logSubject, setLogSubject] = useState('')
  const [logNote, setLogNote] = useState('')
  /* The id, not the row: a reply moves the ticket's status, and a drawer
     holding the copy it opened with would go on showing "Open" against a
     ticket that has just been answered. */
  const [openTicketId, setOpenTicketId] = useState<string | null>(null)
  const [reply, setReply] = useState('')

  const personId = useId()
  const subjectId = useId()
  const noteId = useId()
  const replyId = useId()

  const all = useMemo(() => tickets.data ?? [], [tickets.data])
  /* Counts over the rows actually loaded — which is what a filter pill is. The
     page subtitle deliberately quotes none of them: one page of a cursor-paged
     list is not a platform total. */
  const openCount = all.filter((ticket) => ticket.status === 'OPEN').length
  const answeredCount = all.filter((ticket) => ticket.status === 'ANSWERED').length
  const closedCount = all.filter((ticket) => ticket.status === 'CLOSED').length

  const openTicket = all.find((ticket) => ticket.id === openTicketId) ?? null

  /** The oldest open ticket is the one worth opening a book for. */
  const firstOpenId = all.find((ticket) => ticket.status === 'OPEN')?.id

  const openBook = (slug: string) => navigate(routes.platform.businessBook(slug))

  /**
   * The conversation on the open ticket.
   *
   * Keyed *under* `queryKeys.tickets`, so the invalidation every write below
   * already does reaches the thread as well — a reply that appeared in a toast
   * but not in the transcript above the box is the failure this avoids.
   */
  const thread = useQuery<TicketMessage[], ApiRequestError>({
    queryKey: [...queryKeys.tickets, openTicketId ?? '', 'messages'],
    queryFn: () => get<TicketMessage[]>(`/platform/support/tickets/${openTicketId ?? ''}/messages`),
    enabled: openTicketId !== null,
    retry: shouldRetry,
  })

  const ticketKeys = [queryKeys.tickets, queryKeys.askedAbout]

  const sendReply = useApiMutation<{ ticket: Ticket; body: string }, TicketMessage>({
    run: ({ ticket, body }) =>
      post(`/platform/support/tickets/${ticket.id}/messages`, { body, direction: 'OUT' }),
    invalidates: ticketKeys,
    success: (_message, { ticket }) => t('p.support.replyLogged', { number: ticket.number }),
    onDone: () => setReply(''),
  })

  const closeTicket = useApiMutation<Ticket, ContractTicket>({
    run: (ticket) => patch(`/platform/support/tickets/${ticket.id}`, { status: 'CLOSED' }),
    invalidates: ticketKeys,
    success: (_result, ticket) => t('p.support.ticketClosed', { number: ticket.number }),
    onDone: () => setOpenTicketId(null),
  })

  const reopenTicket = useApiMutation<Ticket, ContractTicket>({
    run: (ticket) => patch(`/platform/support/tickets/${ticket.id}`, { status: 'OPEN' }),
    invalidates: ticketKeys,
    success: (_result, ticket) => t('p.support.ticketReopened', { number: ticket.number }),
  })

  /**
   * "Log a call" — a ticket typed in while the trader is still on the phone.
   *
   * The business is a tenant id, not a slug: `createTicketRequest` takes
   * `tenantId`, and `support_tickets.tenant_id` is NOT NULL, so a call that
   * names no business is refused by the server with the field named (gap A2).
   * The control therefore asks for one rather than letting the reader type a
   * whole conversation and lose it to a 400.
   */
  const logCall = useApiMutation<void, ContractTicket>({
    run: () =>
      post('/platform/support/tickets', {
        tenantId: logBusiness,
        subject: logSubject.trim(),
        source: 'PHONE',
        raisedByName: logPerson.trim(),
        ...(logNote.trim() ? { body: logNote.trim() } : {}),
      }),
    invalidates: ticketKeys,
    success: (ticket) => t('p.support.logged', { number: ticket.number }),
    onDone: () => {
      setLogOpen(false)
      setLogPerson('')
      setLogSubject('')
      setLogNote('')
    },
  })

  const visible = all
    .filter((ticket) => (filter === 'ALL' ? true : ticket.status === filter))
    .filter((ticket) =>
      term === ''
        ? true
        : [ticket.person, ticket.business, ticket.subject].some((field) =>
            field.toLowerCase().includes(term),
          ),
    )

  const columns: Column<Ticket>[] = [
    {
      id: 'from',
      header: t('p.support.colFrom'),
      sortBy: (ticket) => ticket.person,
      cell: (ticket) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={ticket.business} />
          <div className="min-w-0">
            <b className="block font-display font-bold text-dark">{ticket.person}</b>
            <span className="block text-[11.5px] text-muted">
              {ticket.onTrial
                ? t('p.support.businessOnTrial', { business: ticket.business })
                : ticket.business}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'about',
      header: t('p.support.colAbout'),
      cell: (ticket) => ticket.subject,
    },
    {
      id: 'channel',
      header: t('p.support.colCameIn'),
      cell: (ticket) => <StatusBadge>{channelLabel(ticket.channel)}</StatusBadge>,
    },
    {
      id: 'waiting',
      header: t('p.support.colWaiting'),
      cell: (ticket) =>
        // Only an OPEN ticket is still waiting. A closed one showed its elapsed
        // time in warn amber, as though someone were still on the other end.
        ticket.status === 'OPEN' ? (
          <span className="font-display font-bold text-warn">{ticket.waiting}</span>
        ) : (
          <span className="text-muted">{statusLabel(ticket.status)}</span>
        ),
    },
    {
      id: 'do',
      header: t('label.do'),
      numeric: true,
      cell: (ticket) => {
        if (ticket.status !== 'OPEN') {
          return (
            <Button size="sm" onClick={() => setOpenTicketId(ticket.id)}>
              {t('p.support.view')}
            </Button>
          )
        }

        if (ticket.id === firstOpenId) {
          return (
            <Button
              size="sm"
              variant="primary"
              onClick={(event) => {
                event.stopPropagation()
                openBook(ticket.slug)
              }}
            >
              {t('p.support.openHisBook')}
            </Button>
          )
        }

        return (
          <Button size="sm" onClick={() => setOpenTicketId(ticket.id)}>
            {t('act.reply')}
          </Button>
        )
      },
    },
  ]

  /* Every hosted book, not only the ones that already have a ticket: the call
     being logged is usually the first one from that business. */
  const businessOptions = (businesses.data ?? []).map((business) => ({
    value: business.id,
    label: business.name,
  }))

  const logReady =
    logBusiness !== '' &&
    logPerson.trim().length >= 2 &&
    logSubject.trim().length >= 3 &&
    !logCall.isPending

  return (
    <>
      <PageHeader
        title={t('p.support.title')}
        subtitle={t('p.support.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => {
              setLogBusiness(businessOptions[0]?.value ?? '')
              setLogOpen(true)
            }}
          >
            {t('p.support.logCall')}
          </Button>
        }
      />

      <FilterBar>
        <SearchInput placeholder={t('p.support.searchPlaceholder')} />
        <FilterPills<TicketFilter>
          value={filter}
          onChange={setFilter}
          label={t('p.support.title')}
          pills={[
            { value: 'OPEN', label: t('p.support.filterOpen', { count: openCount }) },
            { value: 'ANSWERED', label: t('p.support.filterAnswered', { count: answeredCount }) },
            { value: 'CLOSED', label: t('p.support.filterClosed', { count: closedCount }) },
            { value: 'ALL', label: t('filter.all') },
          ]}
        />
      </FilterBar>

      <div className="grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
        <QueryBoundary
          query={tickets}
          skeleton={<LoadingSkeleton variant="table" rows={4} />}
          isEmpty={(rows) => rows.length === 0}
          empty={
            <Section>
              <EmptyState
                icon={<LifeBuoy className="size-6" />}
                title={t('p.support.emptyTitle')}
                body={t('p.support.emptyBody')}
                action={
                  <Button
                    variant="primary"
                    icon={<Plus className="size-4" />}
                    onClick={() => {
                      setLogBusiness(businessOptions[0]?.value ?? '')
                      setLogOpen(true)
                    }}
                  >
                    {t('p.support.logCall')}
                  </Button>
                }
              />
            </Section>
          }
        >
          {() => (
            <Section>
              <DataTable
                columns={columns}
                rows={visible}
                rowKey={(ticket) => ticket.id}
                caption={t('p.support.title')}
                onRowClick={(ticket) => setOpenTicketId(ticket.id)}
                empty={
                  <EmptyState
                    icon={<LifeBuoy className="size-6" />}
                    title={t('table.noResults')}
                    body={t('p.support.noMatchBody')}
                  />
                }
              />
            </Section>
          )}
        </QueryBoundary>

        <QueryBoundary query={askedAbout} skeleton={<LoadingSkeleton variant="panel" />}>
          {(topics) => (
            <Section title={t('p.support.whatPeopleAsk')}>
              <BarList
                items={topics.map((topic) => ({
                  id: topic.label,
                  label: topic.label,
                  value: topic.count,
                  display: topic.count,
                }))}
              />
              <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
                {t('p.support.askNote')}
              </p>
            </Section>
          )}
        </QueryBoundary>
      </div>

      {/* Log a call — most tickets start as a phone call, and a call that is
          never written down is a ticket nobody can hand over. */}
      <Modal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        title={t('p.support.logCall')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setLogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={!logReady} onClick={() => logCall.mutate()}>
              {t('p.support.log')}
            </Button>
          </>
        }
      >
        <Field label={t('label.business')} hint={t('p.support.businessHint')}>
          <Select
            value={logBusiness}
            onChange={setLogBusiness}
            options={businessOptions}
            ariaLabel={t('label.business')}
          />
        </Field>
        <Field label={t('p.support.who')} hint={t('p.support.whoHint')} htmlFor={personId}>
          <TextInput id={personId} value={logPerson} onChange={setLogPerson} />
        </Field>
        <Field label={t('p.support.colAbout')} htmlFor={subjectId}>
          <TextInput id={subjectId} value={logSubject} onChange={setLogSubject} />
        </Field>
        <Field label={t('p.support.noteLabel')} htmlFor={noteId}>
          <textarea
            id={noteId}
            rows={4}
            value={logNote}
            onChange={(event) => setLogNote(event.target.value)}
            className={CONTROL}
          />
        </Field>
      </Modal>

      {/* The ticket itself. The figures the trader is disputing sit beside his
          own words, so the answer does not have to be reconstructed. */}
      <Drawer
        open={openTicket !== null}
        onClose={() => setOpenTicketId(null)}
        title={
          openTicket
            ? t('p.support.ticketTitle', { number: openTicket.number, person: openTicket.person })
            : ''
        }
        subtitle={openTicket?.business}
        footer={
          openTicket ? (
            <div className="w-full">
              <label htmlFor={replyId} className="sr-only">
                {t('act.reply')}
              </label>
              <textarea
                id={replyId}
                rows={3}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder={t('p.support.replyPlaceholder')}
                className={CONTROL}
              />
              <div className="mt-2.5 flex flex-wrap justify-end gap-2.5">
                {/* Reopen anything that is not open — a CLOSED ticket used to
                    have no way back, and a "Close ticket" button that would
                    close it again. */}
                {openTicket.status === 'OPEN' ? null : (
                  <Button
                    disabled={reopenTicket.isPending}
                    onClick={() => reopenTicket.mutate(openTicket)}
                  >
                    {t('p.support.reopenTicket')}
                  </Button>
                )}
                {openTicket.status === 'CLOSED' ? null : (
                  <Button
                    disabled={closeTicket.isPending}
                    onClick={() => closeTicket.mutate(openTicket)}
                  >
                    {t('p.support.closeTicket')}
                  </Button>
                )}
                <Button
                  variant="primary"
                  disabled={reply.trim() === '' || sendReply.isPending}
                  onClick={() => sendReply.mutate({ ticket: openTicket, body: reply.trim() })}
                >
                  {t('p.support.sendReply')}
                </Button>
              </div>
            </div>
          ) : null
        }
      >
        {openTicket ? (
          <>
            <div className="flex items-center gap-2">
              <StatusBadge tone={STATUS_TONE[openTicket.status]}>
                {statusLabel(openTicket.status)}
              </StatusBadge>
              <StatusBadge>{channelLabel(openTicket.channel)}</StatusBadge>
            </div>

            {openTicket.quote ? (
              <blockquote className="mt-3.5 border-s-2 border-accent-glass-border ps-3.5 text-[13px] leading-relaxed text-sub">
                {openTicket.quote}
              </blockquote>
            ) : null}

            {openTicket.breakdown ? (
              <div className="mt-4">
                <h3 className="font-display text-[13px] font-bold text-dark">
                  {t('p.support.breakdown')}
                </h3>
                <div className="mt-1.5">
                  {openTicket.breakdown.map((line) => (
                    <KeyValue
                      key={line.label}
                      label={line.label}
                      value={<Money paisa={line.amountPaisa} />}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {/* What has actually been said, in order. Everything here was
                recorded; nothing here was delivered — see the header. */}
            <div className="mt-4">
              <h3 className="font-display text-[13px] font-bold text-dark">
                {t('p.support.thread')}
              </h3>
              <QueryBoundary
                query={thread}
                skeleton={<LoadingSkeleton variant="panel" />}
                isEmpty={(rows) => rows.length === 0}
                empty={
                  <p className="mt-1.5 text-[12.5px] text-muted">{t('p.support.threadEmpty')}</p>
                }
              >
                {(rows) => (
                  <ol className="mt-1.5 space-y-2.5">
                    {rows.map((message) => (
                      <li
                        key={message.id}
                        className="rounded-[10px] border border-glass-border bg-glass p-3"
                      >
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-muted">
                          {message.direction === 'IN'
                            ? t('p.support.saidByThem')
                            : t('p.support.saidByUs', {
                                name: message.authorName ?? t('common.none'),
                              })}
                        </p>
                        <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-sub">
                          {message.body}
                        </p>
                      </li>
                    ))}
                  </ol>
                )}
              </QueryBoundary>
              <p className="mt-2.5 text-[11.5px] leading-relaxed text-muted">
                {t('p.support.threadNotDelivered')}
              </p>
            </div>

            <Button
              className="mt-4 w-full"
              icon={<BookOpen className="size-4" />}
              onClick={() => openBook(openTicket.slug)}
            >
              {t('p.support.openToCheck')}
            </Button>
          </>
        ) : null}
      </Drawer>
    </>
  )
}
